import { css } from '@emotion/react';
import type { ButtonSizeStyle } from '@hotjar/webapp/components/Button/button';
import { tokens } from '@hotjar/hotsauce';

export const disabledStyle = css`
  opacity: 0.2;
  pointer-events: auto;
`;

export const defaultStyle = css`
  background-color: ${tokens.color.neutral_600};
  border-radius: ${tokens.radius.m};
  border: 1px solid transparent;
  outline: none;
  color: ${tokens.color.white};
  font-size: 13px;
  font-weight: 600;
  font-family: ${tokens.FONT_FAMILY}, sans-serif, Arial;
  text-transform: capitalize;
  padding: 11px 14px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.15);
  letter-spacing: 0.2px;
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &:focus,
  &:active,
  &.active {
    background: ${tokens.color.neutral_700};
    color: ${tokens.color.white};
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.22);
  }

  &:active,
  &.active {
    ${tokens.elevation(-1)};
  }

  &[disabled] {
    ${disabledStyle}
  }
`;

export const loadingStyle = css`
  color: ${tokens.color.text_default};
  background-color: ${tokens.color.ui_disabled};

  &:hover,
  &:focus,
  &:active,
  &.active,
  &[disabled]:hover {
    color: ${tokens.color.text_default};
    background-color: ${tokens.color.ui_disabled};
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.22);
  }

  &[disabled] {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const primaryStyle = css`
  background-color: ${tokens.color.primary_cta};
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.1);

  &:hover,
  &:focus,
  &:active,
  &.active,
  &[disabled]:hover {
    background-color: ${tokens.color.primary_cta_alt};
  }

  &[disabled] {
    ${disabledStyle}
  }
`;

export const dangerStyle = css`
  background-color: ${tokens.color.danger_cta};

  &:hover,
  &:focus,
  &:active,
  &.active,
  &[disabled]:hover {
    background-color: ${tokens.color.danger_cta_alt};
  }

  &[disabled] {
    ${disabledStyle}
  }
`;

export const cleanStyle = css`
  font-weight: normal;
  color: ${tokens.color.text_default};
  text-transform: none;
  background: none;
  text-shadow: none !important;
  box-shadow: none !important;

  &:hover {
    text-decoration: underline;
  }

  &:hover,
  &:focus,
  &:active,
  &.active,
  &[disabled]:hover {
    background: ${tokens.color.neutral_100} !important;
    color: ${tokens.color.text_default} !important;
  }

  &[disabled] {
    ${disabledStyle}
  }
`;

export const linkStyle = css`
  font-weight: normal;
  text-transform: none;
  background: none;
  text-shadow: none !important;
  box-shadow: none !important;
  padding: 0;
  font-size: inherit;
  color: ${tokens.color.link};

  &:hover,
  &:focus,
  &:active {
    color: ${tokens.color.link_alt};
    text-decoration: underline;
  }

  &:hover,
  &:focus,
  &:active,
  &.active,
  &[disabled]:hover {
    background: transparent;
  }

  &[disabled] {
    ${disabledStyle}
  }
`;

export const secondaryStyle = css`
  text-align: center;
  border: 1px solid ${tokens.color.ui_border};
  color: ${tokens.color.text_default};
  background: transparent;
  padding: 10px 13px;
  box-shadow: none;
  text-shadow: none;

  &:hover {
    border: 1px solid transparent;
  }
`;

export const lineStyle = css`
  color: ${tokens.color.secondary_cta};
  background-color: transparent;
  box-shadow: inset 0 0 0 1px ${tokens.color.secondary_cta};
  text-shadow: none;
  &:hover {
    box-shadow: inset 0 0 0 2px ${tokens.color.secondary_cta_alt};
  }
  &:active {
    ${`${tokens.elevation(-1)}, inset 0 0 0 2px ${tokens.color.secondary_cta_alt}`};
  }
  &:focus {
    ${`${tokens.focus()}, inset 0 0 0 2px ${tokens.color.secondary_cta_alt}`};
  }
  &:focus,
  &:hover,
  &:active {
    background-color: transparent;
    color: ${tokens.color.secondary_cta_alt};
  }
`;

export const iconStyle = css`
  color: ${tokens.color.secondary_cta};
  background-color: transparent;
  box-shadow: inset 0 0 0 1px ${tokens.color.secondary_cta};
  text-decoration: none;
  display: inline-block;
  height: 26px;
  border-radius: ${tokens.radius.m};
  width: 26px;
  border: 1px solid ${tokens.color.ui_border};
  padding: 0;
  vertical-align: middle;
  text-align: center;
  margin: 0 5px;
  direction: ltr;

  &:hover {
    box-shadow: inset 0 0 0 2px ${tokens.color.secondary_cta_alt};
  }
  &:active {
    ${`${tokens.elevation(-1)}, inset 0 0 0 2px ${tokens.color.secondary_cta_alt}`};
  }
  &:focus {
    ${`${tokens.focus()}, inset 0 0 0 2px ${tokens.color.secondary_cta_alt}`};
  }
  &:hover,
  &:active,
  &:focus {
    background: transparent;
    color: ${tokens.color.secondary_cta_alt};
  }
`;

const xsStyles = css`
  font-size: 10px;
  padding: 2px 6px;
  font-weight: 500;
  border-radius: ${tokens.radius.m};
`;

const smStyles = css`
  font-size: 12px;
  padding: 4px 12px;
`;

const lgStyles = css`
  font-size: 20px;
  padding: 18px 38px;
  font-weight: normal;
`;

export const sizeStyle: ButtonSizeStyle = {
  xsmall: xsStyles,
  small: smStyles,
  large: lgStyles,
};
